import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import client from '../utils/sanityClient';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLanguage, t } from '../utils/i18n';
import { glassStyles } from '../utils/glassStyles';

// Arrow components (Copied from ProductCarousel.jsx for consistency)
const NextArrow = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    className="absolute right-[-3rem] md:right-[-4rem] top-1/2 -translate-y-1/2 z-10 p-3 rounded-full hover:bg-white/20 transition-all duration-300 focus:outline-none"
    style={glassStyles.button}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 md:h-8 md:w-8 text-akasha-brown" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </motion.button>
);

const PrevArrow = ({ onClick }) => (
  <motion.button
    onClick={onClick}
    className="absolute left-[-3rem] md:left-[-4rem] top-1/2 -translate-y-1/2 z-10 p-3 rounded-full hover:bg-white/20 transition-all duration-300 focus:outline-none"
    style={glassStyles.button}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 md:h-8 md:w-8 text-akasha-brown" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  </motion.button>
);

// Simplified CategoryCard - no product image, potential for an icon later
const CategoryCard = ({ title, description, link, language }) => {
  return (
    <motion.div
      className="relative overflow-hidden rounded-[2rem] h-64 bg-white/10 backdrop-blur-md border border-white/20 shadow-lg flex flex-col p-6 justify-between"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div> {/* Text content wrapper */}
        {/* Optional: Placeholder for an icon if you want to add one later */}
        {/* <div className="mb-4 text-akasha-brown text-3xl">ICON</div> */}
        <h3 className="text-2xl font-bold text-akasha-brown mb-3">{title}</h3>
        <p className="text-akasha-black/80 text-sm mb-4 flex-grow">{description}</p>
      </div>
      <motion.div>
        <Link
          to={link}
          className="inline-block text-akasha-brown px-6 py-3 rounded-full w-full text-center border border-akasha-brown hover:bg-akasha-brown hover:text-white transition-colors duration-300 mt-auto"
        >
          Ver colección →
        </Link>
      </motion.div>
    </motion.div>
  );
};

const Categories = () => {
  const { language } = useLanguage();
  const [categoriesData, setCategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDirectCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const categories = await client.fetch(`
          *[_type == "category" && defined(name) && defined(slug.current)] | order(name asc) {
            _id,
            "title": name,
            "slugValue": slug.current,
            "descriptionText": description
          }
        `);

        if (!categories || categories.length === 0) {
          console.warn("No categories of type 'category' found or none have both a name and a slug defined.");
          setCategoriesData([]);
        } else {
          const processedCategories = categories.map(cat => ({
            id: cat._id,
            title: cat.title,
            description: cat.descriptionText || (language.code === 'es' 
              ? `Explora nuestra selección de ${cat.title.toLowerCase()}.` 
              : `Explore our selection of ${cat.title.toLowerCase()}.`),
            link: `/catalog?category=${encodeURIComponent(cat.title)}`,
          }));
          setCategoriesData(processedCategories);
        }
      } catch (e) {
        console.error("Failed to fetch direct categories:", e);
        setError(t('categories.fetchError', language.code));
        setCategoriesData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDirectCategories();
  }, [language.code]);

  if (loading) {
    return (
      <section className="relative py-16 bg-transparent">
        <div className="text-center text-akasha-brown">{t('categories.loading', language.code)}</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative py-16 bg-transparent">
        <div className="text-center text-red-500">{error}</div>
      </section>
    );
  }

  if (!categoriesData || categoriesData.length === 0) {
    return (
      <section className="relative py-16 bg-transparent">
        <div className="text-center text-akasha-brown">{t('categories.notFound', language.code)}</div>
      </section>
    );
  }

  const sliderSettings = {
    dots: true,
    infinite: categoriesData.length > 3,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: false,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    dotsClass: "slick-dots custom-dots",
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          infinite: categoriesData.length > 2,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: categoriesData.length > 1,
        }
      }
    ]
  };

  return (
    <section className="relative py-16 bg-transparent">
      <div className="relative z-10 container-custom">
        <h2 className="text-3xl md:text-4xl font-cursive text-akasha-brown text-center mb-6 md:mb-10">
          {t('home.categories.title', language.code)}
        </h2>
        
        <div className="relative px-8 md:px-12">
          <Slider {...sliderSettings}>
            {categoriesData.map((category) => (
              <div key={category.id} className="px-2 pb-4">
                <CategoryCard
                  title={category.title}
                  description={category.description}
                  link={category.link}
                  language={language.code}
                />
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </section>
  );
};

export default Categories; 